import React, { useState } from 'react';
import { Bot, Send, Loader, Calendar, Settings, TrendingUp, AlertTriangle } from 'lucide-react';

const AgenteIAOperacional = ({ dadosAgendamentos, dadosOS }) => {
  const [mensagem, setMensagem] = useState('');
  const [conversa, setConversa] = useState([]);
  const [carregando, setCarregando] = useState(false);

  // Análises pré-configuradas baseadas nos dados operacionais
  const gerarAnaliseAgendamentos = () => {
    if (!dadosAgendamentos?.resumo) return "Dados de agendamentos não disponíveis.";
    
    const { resumo, por_tipo } = dadosAgendamentos;
    const tipoMaior = por_tipo?.find(t => t.percentual === Math.max(...por_tipo.map(p => p.percentual)));
    
    return `📅 **ANÁLISE DE AGENDAMENTOS**

**Performance Geral:**
• Total de agendamentos: ${resumo.total_agendamentos?.toLocaleString('pt-BR')}
• Taxa de conclusão: ${resumo.taxa_conclusao}% (Excelente!)
• Agendamentos pendentes: ${resumo.agendamentos_pendentes}

**Insights Estratégicos:**
• ${tipoMaior?.tipo} representa ${tipoMaior?.percentual}% dos agendamentos
• Com ${resumo.agendamentos_hoje} agendamentos hoje, o ritmo está consistente
• Taxa de conclusão acima de 90% indica excelente execução operacional

**Recomendações:**
1. Manter foco na redução dos ${resumo.agendamentos_pendentes} pendentes
2. Otimizar processo de ${tipoMaior?.tipo} (maior volume)
3. Implementar alertas para agendamentos críticos`;
  };

  const gerarAnaliseOS = () => {
    if (!dadosOS?.resumo) return "Dados de OS não disponíveis.";
    
    const { resumo, por_prioridade } = dadosOS;
    const criticas = por_prioridade?.find(p => p.prioridade === 'Crítica');
    
    return `🔧 **ANÁLISE DE ORDENS DE SERVIÇO**

**Performance Operacional:**
• OS Abertas: ${resumo.total_os_abertas?.toLocaleString('pt-BR')}
• OS Fechadas: ${resumo.total_os_fechadas?.toLocaleString('pt-BR')}
• Taxa de resolução: ${resumo.taxa_resolucao}%
• Tempo médio: ${resumo.tempo_medio_resolucao} dias

**Alertas Críticos:**
• ${criticas?.pendentes} OS críticas pendentes (ATENÇÃO!)
• ${resumo.os_pendentes} OS total pendentes

**Análise de Eficiência:**
• Taxa de resolução de 88.1% está boa, mas pode melhorar
• Tempo médio de 2.3 dias está dentro do aceitável
• Foco necessário em OS críticas e de alta prioridade

**Ações Imediatas:**
1. Priorizar resolução das ${criticas?.pendentes} OS críticas
2. Reduzir tempo médio para menos de 2 dias
3. Implementar escalação automática para OS críticas`;
  };

  const gerarAnaliseComparativa = () => {
    if (!dadosAgendamentos?.resumo || !dadosOS?.resumo) return "Dados insuficientes para análise comparativa.";
    
    return `📊 **ANÁLISE COMPARATIVA OPERACIONAL**

**Correlação Agendamentos x OS:**
• Agendamentos concluídos: ${dadosAgendamentos.resumo.agendamentos_concluidos}
• OS fechadas: ${dadosOS.resumo.total_os_fechadas}
• Eficiência geral: ${((dadosAgendamentos.resumo.taxa_conclusao + dadosOS.resumo.taxa_resolucao) / 2).toFixed(1)}%

**Insights Estratégicos:**
• Agendamentos preventivos podem reduzir OS reativas
• Correlação entre instalações e OS de suporte
• Oportunidade de otimização no fluxo operacional

**Recomendações Integradas:**
1. Alinhar agendamentos preventivos com histórico de OS
2. Criar dashboard unificado de operações
3. Implementar KPIs combinados para melhor gestão`;
  };

  const processarMensagem = async (texto) => {
    setCarregando(true);
    
    // Simular processamento de IA
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    let resposta = '';
    
    // Análise baseada em palavras-chave
    if (texto.toLowerCase().includes('agendamento')) {
      resposta = gerarAnaliseAgendamentos();
    } else if (texto.toLowerCase().includes('os') || texto.toLowerCase().includes('ordem')) {
      resposta = gerarAnaliseOS();
    } else if (texto.toLowerCase().includes('comparar') || texto.toLowerCase().includes('geral')) {
      resposta = gerarAnaliseComparativa();
    } else if (texto.toLowerCase().includes('crítica') || texto.toLowerCase().includes('prioridade')) {
      resposta = `🚨 **ANÁLISE DE PRIORIDADES CRÍTICAS**

**OS Críticas Pendentes:** ${dadosOS?.por_prioridade?.find(p => p.prioridade === 'Crítica')?.pendentes || 0}

**Ações Urgentes:**
1. Escalar imediatamente para equipe sênior
2. Comunicar clientes afetados
3. Ativar protocolo de emergência
4. Monitoramento contínuo até resolução

**Impacto Estimado:** Alto risco de SLA e satisfação do cliente`;
    } else {
      resposta = `🤖 **MOVOPERACIONAL IA**

Olá! Sou especializado em análises operacionais. Posso ajudar com:

📅 **Agendamentos** - Digite "agendamento"
🔧 **Ordens de Serviço** - Digite "OS"  
📊 **Análise Geral** - Digite "geral"
🚨 **Prioridades Críticas** - Digite "crítica"

**Dados Atuais:**
• ${dadosAgendamentos?.resumo?.total_agendamentos || 0} agendamentos
• ${dadosOS?.resumo?.total_os_abertas || 0} OS abertas
• ${dadosOS?.resumo?.os_pendentes || 0} OS pendentes

Como posso ajudar na análise operacional?`;
    }
    
    setCarregando(false);
    return resposta;
  };

  const enviarMensagem = async () => {
    if (!mensagem.trim()) return;
    
    const novaMensagem = mensagem;
    setMensagem('');
    
    // Adicionar mensagem do usuário
    setConversa(prev => [...prev, { tipo: 'usuario', texto: novaMensagem }]);
    
    // Processar e adicionar resposta da IA
    const resposta = await processarMensagem(novaMensagem);
    setConversa(prev => [...prev, { tipo: 'ia', texto: resposta }]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviarMensagem();
    }
  };

  return (
    <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)]">
      <div className="p-4 border-b border-[color:var(--border)] bg-[color:var(--surface-muted)]">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-[color:var(--surface)] rounded-lg border border-[color:var(--border)]">
            <Bot className="h-5 w-5 text-[color:var(--accent)]" />
          </div>
          <div>
            <h3 className="font-semibold text-[color:var(--text-primary)]">MOVOperacional IA</h3>
            <p className="text-sm text-[color:var(--text-muted)]">Análises inteligentes de agendamentos e OS</p>
          </div>
        </div>
      </div>

      {/* Botões de Análise Rápida */}
      <div className="p-4 border-b border-[color:var(--border)] bg-[color:var(--surface-muted)]">
        <p className="text-sm font-medium text-[color:var(--text-primary)] mb-3">Análises Rápidas:</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => processarMensagem('agendamento').then(resp => 
              setConversa(prev => [...prev, { tipo: 'ia', texto: resp }])
            )}
            className="flex items-center space-x-2 px-3 py-2 bg-[color:var(--surface)] text-[color:var(--text-primary)] rounded-lg border border-[color:var(--border)] hover:bg-[color:var(--surface-muted)] transition-colors text-sm"
          >
            <Calendar className="h-4 w-4 text-[color:var(--accent)]" />
            <span>Agendamentos</span>
          </button>
          
          <button
            onClick={() => processarMensagem('os').then(resp => 
              setConversa(prev => [...prev, { tipo: 'ia', texto: resp }])
            )}
            className="flex items-center space-x-2 px-3 py-2 bg-[color:var(--surface)] text-[color:var(--text-primary)] rounded-lg border border-[color:var(--border)] hover:bg-[color:var(--surface-muted)] transition-colors text-sm"
          >
            <Settings className="h-4 w-4 text-[color:var(--status-good)]" />
            <span>Ordens de Serviço</span>
          </button>
          
          <button
            onClick={() => processarMensagem('geral').then(resp => 
              setConversa(prev => [...prev, { tipo: 'ia', texto: resp }])
            )}
            className="flex items-center space-x-2 px-3 py-2 bg-[color:var(--surface)] text-[color:var(--text-primary)] rounded-lg border border-[color:var(--border)] hover:bg-[color:var(--surface-muted)] transition-colors text-sm"
          >
            <TrendingUp className="h-4 w-4 text-[color:var(--accent)]" />
            <span>Análise Geral</span>
          </button>
          
          <button
            onClick={() => processarMensagem('crítica').then(resp => 
              setConversa(prev => [...prev, { tipo: 'ia', texto: resp }])
            )}
            className="flex items-center space-x-2 px-3 py-2 bg-[color:var(--surface)] text-[color:var(--text-primary)] rounded-lg border border-[color:var(--border)] hover:bg-[color:var(--surface-muted)] transition-colors text-sm"
          >
            <AlertTriangle className="h-4 w-4 text-[color:var(--status-bad)]" />
            <span>Críticas</span>
          </button>
        </div>
      </div>

      {/* Área de Conversa */}
      <div className="h-64 overflow-y-auto p-4 space-y-4">
        {conversa.length === 0 && (
          <div className="text-center text-[color:var(--text-muted)] py-8">
            <Bot className="h-8 w-8 mx-auto mb-2 text-[color:var(--text-muted)]" />
            <p className="text-sm">Clique em uma análise rápida ou digite sua pergunta</p>
          </div>
        )}
        
        {conversa.map((msg, index) => (
          <div key={index} className={`flex ${msg.tipo === 'usuario' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
              msg.tipo === 'usuario' 
                ? 'bg-[color:var(--accent)] text-white' 
                : 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]'
            }`}>
              <pre className="text-sm whitespace-pre-wrap font-sans">{msg.texto}</pre>
            </div>
          </div>
        ))}
        
        {carregando && (
          <div className="flex justify-start">
            <div className="bg-[color:var(--surface-muted)] px-4 py-2 rounded-lg">
              <Loader className="h-4 w-4 animate-spin text-[color:var(--accent)]" />
            </div>
          </div>
        )}
      </div>

      {/* Input de Mensagem */}
      <div className="p-4 border-t border-[color:var(--border)]">
        <div className="flex space-x-2">
          <input
            type="text"
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite sua pergunta sobre operações..."
            className="flex-1 px-3 py-2 border border-[color:var(--border)] rounded-lg bg-[color:var(--surface)] text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent"
            disabled={carregando}
          />
          <button
            onClick={enviarMensagem}
            disabled={carregando || !mensagem.trim()}
            className="px-4 py-2 bg-[color:var(--accent)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgenteIAOperacional;

